import { Injectable, computed, effect, signal } from '@angular/core';

export type CartItem = {
  productID: string;
  productName: string;
  quantity: number;
  packageSize: number;
  unitPriceCents: number;
  taxPercentage: number;
};

@Injectable({ providedIn: 'root' })
export class MobileCartService {
  private readonly storageKey = 'bg-imports-mobile-cart-v1';
  public readonly cartItems = signal<CartItem[]>([]);

  public readonly cartCount = computed<number>(() =>
    this.cartItems().reduce((sum, item) => sum + item.quantity, 0),
  );

  public readonly cartSubtotalCents = computed<number>(() =>
    this.cartItems().reduce((sum, item) => sum + item.unitPriceCents * item.quantity, 0),
  );

  public readonly cartTaxCents = computed<number>(() =>
    this.cartItems().reduce((sum, item) => {
      const lineSubTotal = item.unitPriceCents * item.quantity;
      return sum + Math.round((lineSubTotal * item.taxPercentage) / 100);
    }, 0),
  );

  public readonly cartTotalCents = computed<number>(() => this.cartSubtotalCents() + this.cartTaxCents());

  constructor() {
    this.restoreCart();

    effect(() => {
      this.persistCart(this.cartItems());
    });
  }

  public refreshFromStorage(): void {
    this.restoreCart();
  }

  public addOrMergeItem(item: CartItem): void {
    this.upsertItem(item);
  }

  public upsertItem(item: CartItem): void {
    const packageSize = this.normalizePackageSize(item.packageSize);
    const quantity = this.normalizeQuantity(item.quantity, packageSize);
    this.cartItems.update((items) => {
      const existingIndex = items.findIndex((row) => row.productID === item.productID);
      if (existingIndex === -1) {
        return [...items, { ...item, packageSize, quantity }];
      }

      const next = [...items];
      next[existingIndex] = {
        ...next[existingIndex],
        quantity,
        packageSize,
        unitPriceCents: item.unitPriceCents,
        taxPercentage: item.taxPercentage,
      };
      return next;
    });
  }

  public clearCart(): void {
    this.cartItems.set([]);
  }

  public setItemQuantity(productID: string, quantity: number): void {
    this.cartItems.update((items) =>
      items.map((item) =>
        item.productID === productID
          ? {
              ...item,
              quantity: this.normalizeQuantity(quantity, item.packageSize),
            }
          : item,
      ),
    );
  }

  public removeItem(productID: string): void {
    this.cartItems.update((items) => items.filter((item) => item.productID !== productID));
  }

  public lineSubtotalCents(item: CartItem): number {
    return item.unitPriceCents * item.quantity;
  }

  public lineTaxCents(item: CartItem): number {
    return Math.round((this.lineSubtotalCents(item) * item.taxPercentage) / 100);
  }

  public lineTotalCents(item: CartItem): number {
    return this.lineSubtotalCents(item) + this.lineTaxCents(item);
  }

  private restoreCart(): void {
    const raw = this.readStoredCart();
    if (!raw) {
      return;
    }

    try {
      const parsed = JSON.parse(raw);
      const source = Array.isArray(parsed)
        ? parsed
        : Array.isArray(parsed?.items)
          ? parsed.items
          : [];

      if (!Array.isArray(source)) {
        return;
      }

      const restored: CartItem[] = source
        .map((item: any) => ({
          productID: String(item?.productID ?? item?.productId ?? item?.product_id ?? item?.id ?? '').trim(),
          productName: String(item?.productName ?? item?.product_name ?? item?.name ?? item?.title ?? '').trim(),
          packageSize: this.normalizePackageSize(Number(item?.packageSize ?? item?.package_size ?? 1)),
          quantity: Math.max(1, Math.floor(Number(item?.quantity ?? item?.qty ?? 1))),
          unitPriceCents: Math.max(
            0,
            Math.floor(
              Number(
                item?.unitPriceCents ?? item?.unit_price_cents ?? item?.unit_price ?? item?.unitPrice ?? 0,
              ),
            ),
          ),
          taxPercentage: Math.max(0, Number(item?.taxPercentage ?? item?.tax_percentage ?? item?.tax ?? 0)),
        }))
        .filter((item) => item.productID !== '')
        .map((item) => ({
          ...item,
          quantity: this.normalizeQuantity(item.quantity, item.packageSize),
          productName: item.productName || `Item ${item.productID.slice(0, 8)}`,
        }));

      this.cartItems.set(restored);
    } catch {
      // Ignore invalid persisted cart state and start with an empty cart.
    }
  }

  private readStoredCart(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return window.localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }

  private persistCart(items: CartItem[]): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(items));
    } catch {
      // Ignore persistence failures so cart state still works in-memory.
    }
  }

  private normalizePackageSize(rawValue: number): number {
    const size = Math.floor(Number(rawValue) || 1);
    return size > 0 ? size : 1;
  }

  private normalizeQuantity(quantity: number, packageSize: number): number {
    const qty = Math.max(1, Math.floor(Number(quantity) || 1));
    const size = this.normalizePackageSize(packageSize);
    if (size === 1 || qty%size === 0) {
      return qty;
    }
    return Math.ceil(qty / size) * size;
  }
}
